"use client";

import { useResourceEventsForDays } from "@gettactic/hooks";
import { format } from "date-fns";
import React from "react";
import {
  blocksWithin,
  daysAvailabilityStatus,
  type MinuteRange,
  minutesToPickerTime,
  moveSelectionTo,
  resourceDaySummary,
  resourceDayWindow,
  scheduleBusyBlocks,
  selectionRange
} from "@/features/workplace-requests/lib/resourceAvailability";
import { ResourceAvailabilityBar } from "./ResourceAvailabilityBar";

/** The resource a reservation question is asking about, when the form knows it. */
export type ReservationResourceContext = {
  id: string;
  name: string;
  officeId: string;
  /** IANA zone of the resource's office; the days are fetched in it. */
  timeZone: string;
};

export interface ReservationResourceAvailabilityProps {
  resource: ReservationResourceContext;
  /** The days answered so far, one row each. */
  dates: Date[];
  startTime: string;
  endTime: string;
  /**
   * Written when the requester clicks the bar to move the window there, or
   * drags the handle to stretch or move it.
   */
  onChangeTimes?: (times: { startTime: string; endTime: string }) => void;
}

/**
 * Fetches the resource's bookings for every requested day and shows them as
 * an availability bar under the pickers, one row per day.
 *
 * Reads the same `/schedules/occupied` listing the map's reserve card does,
 * so what the requester sees here is what they would see there. A recurring
 * request only gets its listed days checked: a series-wide answer needs its
 * own summary, which is out of scope here.
 */
export const ReservationResourceAvailability: React.FC<
  ReservationResourceAvailabilityProps
> = ({ resource, dates, startTime, endTime, onChangeTimes }) => {
  const days = [...dates].sort(
    (left, right) => left.getTime() - right.getTime()
  );
  const dayKeys = days.map((day) => format(day, "yyyy-MM-dd"));

  const queries = useResourceEventsForDays({
    days: dayKeys,
    officeId: resource.officeId,
    tz: resource.timeZone,
    resourceId: resource.id
  });

  if (days.length === 0) return null;

  const failed = queries.findIndex((query) => query.isError);
  if (failed !== -1) {
    return (
      <p className="text-neutral-500 text-xs" role="status">
        Could not load {resource.name}'s bookings for{" "}
        {format(days[failed], "MMM d")}.
      </p>
    );
  }

  const isPending = queries.some((query) => query.isPending);
  const selection = selectionRange({ startTime, endTime });
  const window = resourceDayWindow(selection);
  const dayBlocks = days.map((day, index) => ({
    day,
    blocks: blocksWithin(
      scheduleBusyBlocks((queries[index]?.data ?? []).flat(), dayKeys[index]),
      window
    )
  }));

  const writeTimes = (range: MinuteRange) =>
    onChangeTimes?.({
      startTime: minutesToPickerTime(range.startMinutes),
      endTime: minutesToPickerTime(range.endMinutes)
    });

  const summary = (): string => {
    if (isPending) return "Loading bookings…";
    if (days.length === 1)
      return resourceDaySummary(dayBlocks[0].blocks, window);
    return `${days.length} days`;
  };

  return (
    <ResourceAvailabilityBar
      resourceName={resource.name}
      heading={
        days.length === 1 ? format(days[0], "MMM d") : `${days.length} days`
      }
      summary={summary()}
      window={window}
      rows={dayBlocks.map((entry, index) => ({
        key: dayKeys[index],
        label: format(entry.day, "MMM d"),
        blocks: entry.blocks
      }))}
      selection={selection}
      onPickMinutes={
        onChangeTimes
          ? (minutes) => writeTimes(moveSelectionTo(minutes, window, selection))
          : undefined
      }
      onChangeSelection={onChangeTimes ? writeTimes : undefined}
      statusOf={
        isPending
          ? undefined
          : (shown) =>
              daysAvailabilityStatus({
                resourceName: resource.name,
                days: dayBlocks,
                selection: shown
              })
      }
    />
  );
};
